import { computed } from 'vue';

import { storeToRefs } from 'pinia';

import dayjs from '@/plugins/dayjs';
import { useAccountStore } from '@/stores/account.js';

export function useBillingGate() {
  const { currentBusinessProfile } = storeToRefs(useAccountStore());

  const plan = computed(() => currentBusinessProfile.value?.plan ?? null);
  const gateState = computed(() => plan.value?.gateState ?? null);
  // Why the gate is up: 'trial' (trial lifecycle) | 'subscription' (dunning) |
  // null (backend predates the field, or no gate) — consumers must fail open on null.
  const gateReason = computed(() => plan.value?.gateReason ?? null);
  const isHardGated = computed(() => gateState.value === 'hard_gate');

  const dataCutoffAt = computed(() => {
    const raw = plan.value?.dataCutoffAt;
    if (!raw) return null;
    const d = raw instanceof Date ? raw : new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  });

  const maxDate = computed(() => dataCutoffAt.value);

  // Returns a clamped copy of a `{ startDate, endDate, granular }` filter so its
  // endDate (and, if needed, startDate) doesn't exceed the billing-gate cutoff.
  // Used by OLAP datasources / dashboard fetchers — the picker UI is untouched
  // and may still let users select past-cutoff ranges; clamping happens at the
  // data layer so backend requests never query data the user can't see.
  const clampDateRangeFilter = (filter) => {
    const cutoff = dataCutoffAt.value;
    if (!cutoff || !filter) return filter;
    const capStr = dayjs(cutoff).format('YYYY-MM-DD');
    let next = filter;
    if (filter.endDate && filter.endDate > capStr) {
      next = { ...next, endDate: capStr };
    }
    const effStart = next.startDate ?? filter.startDate;
    if (effStart && effStart > capStr) {
      next = { ...next, startDate: capStr };
    }
    return next;
  };

  return { gateState, gateReason, dataCutoffAt, isHardGated, maxDate, clampDateRangeFilter };
}
